import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Sparkles, X } from 'lucide-react';
import { getLevelTitle, getLevelUpMessage } from '../utils/motivationalMessages';
import { Button } from './ui/Button';
import { useFocusTrap } from '../hooks/useFocusTrap';
interface LevelUpModalProps {
  isOpen: boolean;
  level: number;
  xp: number;
  onClose: () => void;
}
export function LevelUpModal({
  isOpen,
  level,
  xp,
  onClose
}: LevelUpModalProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const focusTrapRef = useFocusTrap(isOpen);
  // Accessibility: Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  return <AnimatePresence data-id="element-3984">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-3985">
            {/* Confetti Effect */}
            {showConfetti && <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-3986">
                {[...Array(50)].map((_, i) => <motion.div key={i} className="absolute w-3 h-3 rounded-full" style={{
            background: ['#fbbf24', '#f59e0b', '#4f46e5', '#10b981', '#fb7185'][i % 5],
            left: `${Math.random() * 100}%`,
            top: '-10%'
          }} initial={{
            y: 0,
            opacity: 1,
            rotate: 0
          }} animate={{
            y: window.innerHeight + 100,
            opacity: 0,
            rotate: 360,
            transition: {
              duration: 2 + Math.random() * 2,
              delay: Math.random() * 0.5,
              ease: 'easeOut'
            }
          }} data-id="element-3987" />)}
              </div>}

            {/* Modal */}
            <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="levelup-title" initial={{
          scale: 0.5,
          opacity: 0,
          rotateY: -180
        }} animate={{
          scale: 1,
          opacity: 1,
          rotateY: 0
        }} exit={{
          scale: 0.5,
          opacity: 0,
          rotateY: 180
        }} transition={{
          type: 'spring',
          damping: 20,
          stiffness: 200
        }} className="bg-white rounded-[2.5rem] p-10 max-w-md w-full relative shadow-2xl border-4 border-white" onClick={e => e.stopPropagation()} data-id="element-3988">
              <button onClick={onClose} className="absolute top-6 right-6 p-2 bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors" aria-label="Sluiten" data-id="element-3989">
                <X className="w-6 h-6" data-id="element-3990" />
              </button>

              <div className="text-center" data-id="element-3991">
                {/* Animated Trophy */}
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              delay: 0.2,
              type: 'spring',
              damping: 10
            }} className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-amber-300 to-orange-400 rounded-full mb-8 shadow-xl shadow-amber-200 border-4 border-white relative" data-id="element-3992">
                  <Trophy className="w-16 h-16 text-white drop-shadow-md" data-id="element-3993" />

                  {/* Sparkles around trophy */}
                  {[0, 1, 2, 3].map(i => <motion.div key={i} className="absolute" style={{
                top: '50%',
                left: '50%',
                transform: `rotate(${i * 90}deg) translateY(-60px)`
              }} animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2
              }} data-id="element-3994">
                      <Sparkles className="w-6 h-6 text-amber-400 fill-amber-300" data-id="element-3995" />
                    </motion.div>)}
                </motion.div>

                <motion.h2 id="levelup-title" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-4xl font-black text-slate-900 mb-2 tracking-tight" data-id="element-3996">
                  Level Up! 🎉
                </motion.h2>

                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.4
            }} className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-5 py-2 rounded-full mb-6 border border-indigo-200" data-id="element-3997">
                  <Star className="w-5 h-5 fill-indigo-500" data-id="element-3998" />
                  <span className="font-black text-xl" data-id="element-3999">Level {level}</span>
                </motion.div>

                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} className="text-2xl font-black text-amber-500 mb-4" data-id="element-4000">
                  {getLevelTitle(level)}
                </motion.p>

                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} className="text-slate-500 mb-8 text-lg font-medium leading-relaxed" data-id="element-4001">
                  {getLevelUpMessage(level)}
                </motion.p>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7
            }} className="bg-slate-50 rounded-2xl p-4 mb-8 border border-slate-100" data-id="element-4002">
                  <div className="flex items-center justify-center gap-3 text-slate-700" data-id="element-4003">
                    <Star className="w-6 h-6 text-amber-400 fill-amber-400" data-id="element-4004" />
                    <span className="font-black text-xl" data-id="element-4005">{xp} XP</span>
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.8
            }} data-id="element-4006">
                  <Button onClick={onClose} variant="primary" size="xl" fullWidth className="shadow-xl shadow-indigo-200" data-id="element-4007">
                    Doorgaan met oefenen! 🚀
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}